import { Component, type ReactNode, type ErrorInfo } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error.message, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="flex items-center justify-center min-h-[400px] p-8">
          <div className="text-center max-w-md">
            <div className="w-14 h-14 mx-auto rounded-full bg-rose-500/15 flex items-center justify-center mb-4">
              <i className="ri-error-warning-line text-rose-400 text-2xl" />
            </div>
            <h2 className="text-lg font-bold text-foreground-950 mb-2">Something went wrong</h2>
            <p className="text-sm text-foreground-500 mb-4">
              {this.state.error?.message || 'An unexpected error occurred while rendering this page.'}
            </p>
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              className="px-5 py-2.5 rounded-xl bg-accent-500 text-white text-sm font-semibold hover:bg-accent-400 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line mr-1.5" /> Try Again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}